/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, useTranslations } from "@i18n/next";
import type { ParishSearchItem } from "@packages/sdk";
import { requestParishSearch } from "@packages/sdk";
import type { ComboboxOption, ThemeType } from "@packages/ui/emotion";
import {
  Button,
  ComboboxSearchAsync,
  cssMediaQueryMinWidthTablet,
  spacing,
  Text,
} from "@packages/ui/emotion";
import { useState } from "react";

const cssRequest: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  opacity: 0,
  pointerEvents: "none" as const,
  transition: "opacity 250ms ease-in-out",
  willChange: "opacity",
});

const cssVisibleRequest: Interpolation<ThemeType> = {
  opacity: 1,
  pointerEvents: "auto" as const,
};

const cssParish: Interpolation<ThemeType> = (theme) => ({
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "flex-start",
  rowGap: theme.spacing.t,
  textAlign: "left" as const,
  width: theme.spacing.full,
});

const cssNoWrap: Interpolation<ThemeType> = (theme) => ({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
  width: theme.spacing.full,
});

const cssAddress: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
});

const cssLink: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  textDecoration: "underline",
});

const cssCommandItem: Interpolation<ThemeType> = (theme) => ({
  paddingInline: theme.spacing.l,
});

const cssSearch: Interpolation<ThemeType> = () => ({
  paddingInline: spacing.none,
  width: spacing.full,
  [cssMediaQueryMinWidthTablet]: {
    paddingInline: spacing.xxl,
  },
});

const transformParishResult = (
  result: ParishSearchItem,
): ComboboxOption<number> => ({
  value: result.id.raw,
  text: result.name.raw,
  label: (
    <div css={cssParish}>
      <Text as="span" type="title" size="m" css={cssNoWrap}>
        {result.name.raw}
      </Text>
      <Text as="span" type="detail" size="l" css={[cssAddress, cssNoWrap]}>
        {result.street_address.raw}, {result.city.raw}, {result.region.raw}{" "}
        {result.postal_code.raw}
      </Text>
    </div>
  ),
});

const fetchParishOptions = async (
  search: string,
): Promise<ComboboxOption<number>[]> => {
  const response = await requestParishSearch(search);
  return response.results.map(transformParishResult);
};

export const ParishSearch = () => {
  const t = useTranslations();
  const [inputValue, setInputValue] = useState("");
  const [selectedParishId, setSelectedParishId] = useState<number | undefined>(
    undefined,
  );

  const showHelpText = inputValue.length > 0;

  return (
    <>
      <Text
        as="p"
        type="detail"
        size="xl"
        css={[cssRequest, showHelpText && cssVisibleRequest]}
      >
        {t("web_parish_dont_see")}{" "}
        <Link href="https://help.hallow.com" css={cssLink}>
          {t("web_parish_request_to_add")}
        </Link>
      </Text>
      <div css={cssSearch}>
        <ComboboxSearchAsync
          value={selectedParishId}
          onChange={setSelectedParishId}
          inputValue={inputValue}
          onInputChange={setInputValue}
          getOptions={fetchParishOptions}
          config={{
            placeholder: t("parish_search_bar_hint"),
            showSelectedLabel: true,
          }}
          customCss={{
            commandItem: cssCommandItem,
          }}
        />
      </div>
      <Button asChild disabled={!selectedParishId} size="l">
        <Link href={`/communities/${selectedParishId}`}>
          {t("community_flag_continue")}
        </Link>
      </Button>
    </>
  );
};
